/**
 * Apple 动效预设（Variants / 页面过渡 / 列表 stagger）（通用 · 跨主题/跨 shell）
 * ------------------------------------------------------------------
 * 把 Apple「continuity」原则常见的动效组合固化为可复用 framer-motion `Variants`，
 * 与 [[liquidSpring]]（spring 物理参数）配套。任意 shell/主题复用同一手感。
 *
 *  - `liquidPageVariants`：路由内容切换的过渡。macOS Tahoe 26 / iOS 26 的内容切换是
 *    「交叉淡入 + 极轻微缩放」有机运动（非水平 push、非硬切），用 smooth spring。
 *  - `staggerContainer` / `staggerItem`：列表/网格入场级联（iOS 列表出现签名）。
 *
 * 用法（页面过渡，见 liquid-shell.tsx）：
 *   <motion.div key={pathname} variants={liquidPageVariants}
 *     initial={reduce ? false : 'initial'} animate="animate" exit={reduce ? undefined : 'exit'} />
 *
 * 用法（列表 stagger）：
 *   <motion.ul variants={staggerContainer()} initial="hidden" animate="show">
 *     {items.map(it => <motion.li key={it.id} variants={staggerItem}>...</motion.li>)}
 *   </motion.ul>
 */
import { type Variants } from 'framer-motion';
import { liquidSpring, liquidEase } from './springs';

/** 路由内容切换：交叉淡入 + 微缩放 + 轻微上移（macOS Tahoe / iOS 26 内容过渡签名）。 */
export const liquidPageVariants: Variants = {
  initial: { opacity: 0, y: 10, scale: 0.992 },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: liquidSpring.smooth,
  },
  exit: {
    opacity: 0,
    scale: 0.998,
    transition: { duration: 0.18, ease: liquidEase.outExpo },
  },
};

/** 列表/网格入场：容器按 staggerChildren 级联触发子项。 */
export function staggerContainer(stagger = 0.045, delayChildren = 0.05): Variants {
  return {
    hidden: {},
    show: {
      transition: { staggerChildren: stagger, delayChildren },
    },
  };
}

/** 列表/网格子项入场：上移 + 微缩放 + 淡入，smooth spring。 */
export const staggerItem: Variants = {
  hidden: { opacity: 0, y: 10, scale: 0.985 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: liquidSpring.smooth,
  },
};

/** reduced-motion 下的静态子项/容器（瞬切，无位移）。 */
export const staggerItemStatic: Variants = {
  hidden: {},
  show: {},
};

export default liquidPageVariants;
